import {
	AfterViewInit,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	inject,
	NgZone,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Signal,
	signal,
	SimpleChanges,
	ViewChild,
	WritableSignal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {catchError, finalize, of, skip, switchMap, take} from 'rxjs';
import { SharedModule } from '../../shared/shared.module';
import {
	DeleteMessagesResult, SetPropertiesResult,
	UploadAnnouncementResult,
	VMailboxWidgetService
} from '../services/vmailbox-widget.service';
import {
	AnnouncementInfo,
	AnnouncementType,
	AsteriskVoicemail,
	Voicemessage,
} from '../../shared/services/db/asteriks-entities.interface';
import {
	IAppDashboard,
	IPermissionsFlags,
	IPluginPermissions,
	IUser,
	ModelService,
	PluginInjectorService,
	PluginInjectorWidget,
} from 'core-client/api';
import { IVMailboxWidget, IVMailboxWidgetPermissions } from '../interfaces/vmailbox-widget.interface';
import { VoiceMailboxStateUpdateItem } from '../utils/vmailbox-socket.constants';

@Component({
	selector: 'lib-vmailbox-widget-view',
	imports: [SharedModule],
	templateUrl: './vmailbox-widget-view.component.html',
	styleUrl: './vmailbox-widget-view.component.scss',
})
export class VMailboxWidgetViewComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
	protected readonly vmailbox = inject(VMailboxWidgetService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly modelService = inject(ModelService);
	private readonly pluginInjectorService = inject(PluginInjectorService);
	private readonly translate = inject(TranslateService);
	private readonly ngZone = inject(NgZone);

	@Input() data: PluginInjectorWidget.IWidget | undefined;
	@Input() isHeaderHovered: boolean = false;
	/** Menu-item mode: pass mailbox number directly, bypasses AppPluginWidget entirely. */
	@Input() menuItemMailboxNr?: string;
	/** Menu-item mode: stable widget ID generated by the caller. */
	@Input() menuItemWidgetId?: string;
	@ViewChild('voiceMailWidgetContainer', { static: false })
	protected voiceMailWidgetContainer!: ElementRef;

	protected vmailboxWidget: IVMailboxWidget | undefined;
	protected widgetId: string | undefined;
	protected currDashboardId: string | undefined;
	protected widgetPermissions: IVMailboxWidgetPermissions = {
		widgetEnabled: false,
		widgetSettingsEditEnabled: false,
	};
	private resizeObserver: ResizeObserver | undefined;

	// The currently subscribed mailbox
	private subscribedMailbox: AsteriskVoicemail | null = null;

	// Signals - mailbox
	readonly selectedVoicemail: WritableSignal<AsteriskVoicemail | null> = signal(null);
	readonly isLoading: WritableSignal<boolean> = signal(false);
	readonly error: WritableSignal<string | null> = signal(null);
	readonly isNotConfigured: WritableSignal<boolean> = signal(false);

	// Signals - messages
	readonly messages: WritableSignal<Voicemessage[]> = signal([]);
	readonly isLoadingMessages: WritableSignal<boolean> = signal(false);
	readonly messagesError: WritableSignal<string | null> = signal(null);
	readonly audioUrls: WritableSignal<Map<number, string>> = signal(new Map());
	readonly playingMessageId: WritableSignal<number | null> = signal(null);
	readonly audioLoadingId: WritableSignal<number | null> = signal(null);
	readonly messageErrors: WritableSignal<Map<number, string>> = signal(new Map());
	readonly announcementErrors: WritableSignal<Map<AnnouncementType, string>> = signal(new Map());

	// Signals - announcements
	readonly selectedTabIndex: WritableSignal<number> = signal(0);
	readonly announcementInfo: WritableSignal<Map<AnnouncementType, AnnouncementInfo>> = signal(new Map());
	readonly announcementAudioUrls: WritableSignal<Map<AnnouncementType, string>> = signal(new Map());
	readonly playingAnnouncementType: WritableSignal<AnnouncementType | null> = signal(null);
	readonly announcementLoadingType: WritableSignal<AnnouncementType | null> = signal(null);
	readonly uploadingType: WritableSignal<AnnouncementType | null> = signal(null);

	// Signals - recorder
	readonly recordingType: WritableSignal<AnnouncementType | null> = signal(null);
	readonly recordingSeconds: WritableSignal<number> = signal(0);
	readonly requestingMic: WritableSignal<boolean> = signal(false);
	readonly recorderError: WritableSignal<string | null> = signal(null);

	private mediaRecorder: MediaRecorder | null = null;
	private recordedChunks: Blob[] = [];
	private recordingTimer: ReturnType<typeof setInterval> | null = null;
	private micStream: MediaStream | null = null;

	// Computed
	readonly unreadCount: Signal<number> = computed(
		() => this.messages().filter((m: Voicemessage) => m.isnew).length,
	);
	readonly announcementTypes: AnnouncementType[] = ['temp', 'default', 'busy', 'unavail'];

	ngOnInit(): void {
		if (this.menuItemMailboxNr !== undefined && this.menuItemWidgetId !== undefined) {
			this.widgetPermissions = { widgetEnabled: true, widgetSettingsEditEnabled: false };
		}
	}

	ngAfterViewInit(): void {
		this.initAfterView().catch((err: unknown) => {
			const message = err instanceof Error ? err.message : String(err);
			console.error(`[VMailbox Widget] initAfterView threw: ${message}`);
			this.error.set(this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.WIDGET_INIT'));
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['isHeaderHovered']) {
			this.setWidgetHeaderOnHover(changes['isHeaderHovered'].currentValue as boolean);
		}
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
		this.stopRecorderCleanup();
		if (this.widgetId) {
			this.vmailbox.destroyWidget(this.widgetId);
		} else {
			console.warn('[VMailbox Widget] ngOnDestroy — widgetId not set, socket cleanup skipped');
		}
	}

	/**
	 * Called when the settings dialog saves a new mailboxNr.
	 */
	private reloadFromSettings(mailboxNr: string | undefined): void {
		console.log(`[VMailbox Widget ${this.widgetId}] reloadFromSettings — mailboxNr=${mailboxNr ?? 'none'}`);

		if (this.widgetId && this.currDashboardId) {
			this.vmailbox.initializeWidget(this.widgetId, this.currDashboardId);
		} else {
			console.warn(`[VMailbox Widget ${this.widgetId}] reloadFromSettings — widgetId or currDashboardId missing, socket re-registration skipped`);
		}

		// Keep the local widget settings object in sync so refresh() works correctly
		if (this.vmailboxWidget) {
			this.vmailboxWidget.settings.mailboxNr = mailboxNr;
		}

		// Clear tracked mailbox so reconnect handler doesn't re-subscribe old mailbox
		this.subscribedMailbox = null;

		// Reset all data state
		this.error.set(null);
		this.messagesError.set(null);
		this.selectedVoicemail.set(null);
		this.messages.set([]);
		this.audioUrls.set(new Map());
		this.messageErrors.set(new Map());
		this.announcementErrors.set(new Map());
		this.announcementInfo.set(new Map());
		this.announcementAudioUrls.set(new Map());

		if (!mailboxNr) {
			console.log(`[VMailbox Widget ${this.widgetId}] reloadFromSettings — no mailboxNr, switching to not-configured state`);
			this.isNotConfigured.set(true);
			return;
		}

		this.isNotConfigured.set(false);
		this.loadMailbox(mailboxNr);
	}

	private onSocketConnected(): void {
		if (this.subscribedMailbox) {
			console.log(`[VMailbox Widget ${this.widgetId}] Socket connected — subscribing mailboxId=${this.subscribedMailbox.id} nr=${this.subscribedMailbox.nr}`);
			this.vmailbox.subscribeToMailboxCounts(this.subscribedMailbox.id, this.subscribedMailbox.nr, this.widgetId!);
		}
	}

	private onSocketDisconnected(): void {
		console.log(`[VMailbox Widget ${this.widgetId}] Socket disconnected`);
	}

	private async initAfterView(): Promise<void> {
		const user = this.modelService._user.getUser();
		if (!user?._id) {
			console.error('[VMailbox Widget] initAfterView — user not found');
			return;
		}

		// ── Menu-item mode ──────────────────────────────────────────────────────
		// mailboxNr and widgetId are provided directly; no AppPluginWidget involved.
		if (this.menuItemMailboxNr !== undefined && this.menuItemWidgetId !== undefined) {
			await this.initMenuItemMode(user, this.menuItemMailboxNr, this.menuItemWidgetId);
			return;
		}

		// ── Dashboard widget mode ────────────────────────────────────────────────
		if (!this.data?.appPluginWidget) {
			console.error('[VMailbox Widget] initAfterView — appPluginWidget missing');
			return;
		}

		await this.getWidgetAccessRights(user, this.pluginInjectorService, this.data);

		this.vmailboxWidget = this.data.appPluginWidget as IVMailboxWidget;
		this.widgetId = this.vmailboxWidget?._id;
		this.currDashboardId = user.active_dashboard;

		if (!this.widgetId) {
			console.error('[VMailbox Widget] initAfterView — widgetId missing on appPluginWidget');
			return;
		}

		if (!this.currDashboardId) {
			console.error(`[VMailbox Widget ${this.widgetId}] initAfterView — active_dashboard missing on user`);
			return;
		}

		// Register with the socket service — must happen before any subscriptions
		this.vmailbox.initializeWidget(this.widgetId, this.currDashboardId);

		this.vmailbox.getMailboxNr$(this.widgetId).pipe(
			takeUntilDestroyed(this.destroyRef),
			skip(1),
		).subscribe((mailboxNr: string | undefined) => {
			this.reloadFromSettings(mailboxNr);
		});

		this.vmailbox.getConnectionState().pipe(
			takeUntilDestroyed(this.destroyRef),
		).subscribe((state) => {
			this.ngZone.run(() => {
				console.log(`[VMailbox Widget ${this.widgetId}] Socket connection state: ${state}`);
				if (state === 'connected') {
					this.onSocketConnected();
				} else if (state === 'disconnected') {
					this.onSocketDisconnected();
				}
			});
		});

		// Register ResizeObserver now that the view is initialised
		this.observeStyleChanges();

		const mailboxNr = this.vmailboxWidget.settings?.mailboxNr;
		if (!mailboxNr) {
			console.warn(`[VMailbox Widget ${this.widgetId}] initAfterView — no mailboxNr in settings, widget not configured`);
			this.isNotConfigured.set(true);
			return;
		}

		this.loadMailbox(mailboxNr);
	}

	private async initMenuItemMode(user: IUser, mailboxNr: string, widgetId: string): Promise<void> {
		this.widgetId = widgetId;
		this.currDashboardId = user.active_dashboard;

		if (!this.currDashboardId) {
			console.error(`[VMailbox Widget ${this.widgetId}] initMenuItemMode — active_dashboard missing on user`);
			return;
		}

		this.vmailbox.initializeWidget(this.widgetId, this.currDashboardId);

		this.vmailbox.getConnectionState().pipe(
			takeUntilDestroyed(this.destroyRef),
		).subscribe((state) => {
			this.ngZone.run(() => {
				console.log(`[VMailbox Widget ${this.widgetId}] Socket connection state: ${state}`);
				if (state === 'connected') {
					this.onSocketConnected();
				} else if (state === 'disconnected') {
					this.onSocketDisconnected();
				}
			});
		});

		this.observeStyleChanges();
		this.loadMailbox(mailboxNr);
	}

	private observeStyleChanges(): void {
		if (!this.voiceMailWidgetContainer?.nativeElement) {
			console.warn('[VMailbox Widget] observeStyleChanges — voiceMailWidgetContainer not yet available');
			return;
		}

		this.resizeObserver = new ResizeObserver(() => {
			this.setWidgetHeaderOnHover(this.isHeaderHovered);
		});

		this.resizeObserver.observe(this.voiceMailWidgetContainer.nativeElement);
	}

	private setWidgetHeaderOnHover(isHeaderHovered: boolean): void {
		if (this.voiceMailWidgetContainer?.nativeElement) {
			this.voiceMailWidgetContainer.nativeElement.style.marginTop = isHeaderHovered ? '35px' : '0';
		}
	}

	private async getWidgetAccessRights(
		user: IUser,
		pluginInjectorService: PluginInjectorService,
		widget: PluginInjectorWidget.IWidget,
	): Promise<void> {
		const appDashboard = user.app_dashboards.find(
			(d: IAppDashboard) => d._id === user.active_dashboard,
		);
		if (!appDashboard) {
			console.warn('[VMailbox Widget] getWidgetAccessRights — active dashboard not found');
			return;
		}

		const pluginPermissions = await pluginInjectorService.permissionService.getPermissions(
			appDashboard, user,
		);
		const accessRights = pluginPermissions.find(
			(p: IPluginPermissions) => p.plugin_id === widget?.pluginId,
		);
		if (!accessRights?.widget_permissions) {
			console.warn(`[VMailbox Widget] getWidgetAccessRights — no widget_permissions found for pluginId=${widget?.pluginId}`);
			return;
		}

		const modulePermissions = accessRights.widget_permissions[
			widget.pluginModule.toLowerCase()
			] as IPermissionsFlags;

		const widgetEnabled = widget?.widgetPermissions?.widgetEnabledPermission?.permissionRef;
		const widgetSettingsEnabled =
			widget?.widgetPermissions?.widgetBasePermissions?.widgetSettingsEnabledPermission?.permissionRef;

		this.widgetPermissions = {
			widgetEnabled: widgetEnabled ? modulePermissions[widgetEnabled] : false,
			widgetSettingsEditEnabled: widgetSettingsEnabled ? modulePermissions[widgetSettingsEnabled] : false,
		};
	}

	private toUserMessage(err: HttpErrorResponse, fallback: string): string {
		const t = (key: string) => this.translate.instant(key);
		switch (err.status) {
			case 0:   return t('ASTIMAX.VMAILBOX.WIDGET.ERRORS.NO_CONNECTION');
			case 401: return t('ASTIMAX.VMAILBOX.WIDGET.ERRORS.SESSION_EXPIRED');
			case 403: return t('ASTIMAX.VMAILBOX.WIDGET.ERRORS.ACCESS_DENIED');
			case 404: return t('ASTIMAX.VMAILBOX.WIDGET.ERRORS.NOT_FOUND');
			case 422: {
				const detail = err.error?.message as string | undefined;
				return detail ?? fallback;
			}
			case 500: return t('ASTIMAX.VMAILBOX.WIDGET.ERRORS.SERVER_ERROR');
			case 503: return t('ASTIMAX.VMAILBOX.WIDGET.ERRORS.DB_UNAVAILABLE');
			default:  return fallback;
		}
	}

	private isSilent(err: HttpErrorResponse): boolean {
		return err.status === 403 || err.status === 404;
	}

	private loadMailbox(mailboxNr: string): void {
		this.isLoading.set(true);
		this.error.set(null);

		this.vmailbox.getMailbox(mailboxNr).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				console.error(`[VMailbox Widget ${this.widgetId}] loadMailbox failed: HTTP ${err.status} nr=${mailboxNr}`);
				if (!this.isSilent(err)) {
					this.error.set(this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.MAILBOXES_LOAD')));
				}
				return of(this.buildPlaceholderMailbox(mailboxNr));
			}),
			finalize(() => this.isLoading.set(false)),
		).subscribe((mailbox: AsteriskVoicemail) => {
			this.selectedVoicemail.set(mailbox);
			if (!this.error()) {
				this.loadMessages(mailbox.nr);
				this.loadAllAnnouncementInfo(mailbox.nr);
				this.setupSocketSubscription(mailbox);
			}
		});
	}

	private setupSocketSubscription(mailbox: AsteriskVoicemail): void {
		if (!this.widgetId) {
			console.warn('[VMailbox Widget] setupSocketSubscription — widgetId not set, skipping');
			return;
		}
		if (mailbox.id === 0) {
			console.warn(`[VMailbox Widget ${this.widgetId}] setupSocketSubscription — mailbox.id=0 (placeholder/error state), skipping`);
			return;
		}

		this.subscribedMailbox = mailbox;
		this.vmailbox.setLastKnownName(this.widgetId, mailbox.name ?? '');
		console.log(`[VMailbox Widget ${this.widgetId}] setupSocketSubscription — mailboxId=${mailbox.id} nr=${mailbox.nr} (will subscribe once connected)`);

		this.vmailbox.getConnectionState().pipe(
			take(1),
			takeUntilDestroyed(this.destroyRef),
		).subscribe((connState) => {
			if (connState === 'connected') {
				this.onSocketConnected();
			}
		});

		this.vmailbox.onMessageCountUpdate(this.widgetId).pipe(
			takeUntilDestroyed(this.destroyRef),
		).subscribe((update: VoiceMailboxStateUpdateItem) => {
			console.log(`[VMailbox Widget ${this.widgetId}] Count update mailbox=${update.number} new=${update.messages.new} old=${update.messages.oldM}`);
			this.loadMessages(mailbox.nr);
		});

		this.vmailbox.onMailboxDeleted(this.widgetId).pipe(
			takeUntilDestroyed(this.destroyRef),
		).subscribe(() => {
			console.warn(`[VMailbox Widget ${this.widgetId}] Mailbox ${mailbox.nr} deleted externally`);
			this.error.set(this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.NOT_FOUND'));
			this.messages.set([]);
		});

		this.vmailbox.onMessageDeleted(this.widgetId).pipe(
			takeUntilDestroyed(this.destroyRef),
		).subscribe(({ messageId }) => {
			console.log(`[VMailbox Widget ${this.widgetId}] Message ${messageId} deleted externally`);
			this.messages.update((msgs: Voicemessage[]) =>
				msgs.filter((m: Voicemessage) => m.id !== messageId),
			);
			if (this.playingMessageId() === messageId) this.stopMessage();
		});

		this.vmailbox.onMailboxNameChanged(this.widgetId).pipe(
			takeUntilDestroyed(this.destroyRef),
		).subscribe((newName: string) => {
			console.log(`[VMailbox Widget ${this.widgetId}] Mailbox name changed to "${newName}"`);
			const current = this.selectedVoicemail();
			if (current) {
				this.selectedVoicemail.set({ ...current, name: newName });
			}
		});
	}

	private loadMessages(mailboxNr: string): void {
		this.isLoadingMessages.set(true);
		this.messagesError.set(null);
		this.messages.set([]);
		this.messageErrors.set(new Map());

		this.vmailbox.getVoicemessages(mailboxNr).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				console.error(`[VMailbox Widget ${this.widgetId}] loadMessages failed: HTTP ${err.status} nr=${mailboxNr}`);
				if (!this.isSilent(err)) this.messagesError.set(this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.MESSAGES_LOAD')));
				return of([] as Voicemessage[]);
			}),
			finalize(() => this.isLoadingMessages.set(false)),
		).subscribe((msgs: Voicemessage[]) => this.messages.set(msgs));
	}

	playMessage(message: Voicemessage): void {
		if (!this.widgetId) {
			console.warn('[VMailbox Widget] playMessage — widgetId not set');
			return;
		}

		this.stopAnnouncement();
		this.messageErrors.update((m: Map<number, string>) => { const u = new Map(m); u.delete(message.id); return u; });

		const existing: string | undefined = this.audioUrls().get(message.id);
		if (existing) {
			this.playingMessageId.set(message.id);
			this.autoMarkRead(message);
			return;
		}

		this.audioLoadingId.set(message.id);
		this.vmailbox.getMessageAudioUrl(this.widgetId, message.id).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				console.error(`[VMailbox Widget ${this.widgetId}] getMessageAudioUrl failed: HTTP ${err.status} messageId=${message.id}`);
				const msg = err.status === 404
					? this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.NO_RECORDING')
					: this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.AUDIO_LOAD'));
				this.messageErrors.update((m: Map<number, string>) => new Map(m).set(message.id, msg));
				return of(null);
			}),
			finalize(() => this.audioLoadingId.set(null)),
		).subscribe((url: string | null) => {
			if (!url) {
				console.log(`[VMailbox Widget ${this.widgetId}] Unable to play message, no audio URL`);
				return;
			}
			this.audioUrls.update((m: Map<number, string>) => new Map(m).set(message.id, url));
			this.playingMessageId.set(message.id);
			this.autoMarkRead(message);
		});
	}

	stopMessage(): void { this.playingMessageId.set(null); }

	markAsRead(message: Voicemessage): void {
		if (!message.isnew) return;
		this.vmailbox.setVoicemessagesProperties([{ id: message.id, isRead: true }]).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				console.error(`[VMailbox Widget ${this.widgetId}] markAsRead failed: HTTP ${err.status} messageId=${message.id}`);
				this.messageErrors.update((m: Map<number, string>) =>
					new Map(m).set(message.id, this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.STATUS_UPDATE'))),
				);
				return of(null);
			}),
		).subscribe((result: SetPropertiesResult | null) => {
			if (result === null) {
				console.log(`[VMailbox Widget ${this.widgetId}] markAsRead failed: no result for messageId=${message.id}`);
				return;
			}
			this.messages.update((msgs: Voicemessage[]) =>
				msgs.map((m: Voicemessage) => m.id === message.id ? { ...m, isnew: false } : m),
			);
		});
	}

	markAsUnread(message: Voicemessage): void {
		if (message.isnew) return;
		this.vmailbox.setVoicemessagesProperties([{ id: message.id, isRead: false }]).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				console.error(`[VMailbox Widget ${this.widgetId}] markAsUnread failed: HTTP ${err.status} messageId=${message.id}`);
				this.messageErrors.update((m: Map<number, string>) =>
					new Map(m).set(message.id, this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.STATUS_UPDATE'))),
				);
				return of(null);
			}),
		).subscribe((result: SetPropertiesResult | null) => {
			if (result === null) return;
			this.messages.update((msgs: Voicemessage[]) =>
				msgs.map((m: Voicemessage) => m.id === message.id ? { ...m, isnew: true } : m),
			);
		});
	}

	deleteMessage(message: Voicemessage): void {
		this.messageErrors.update((m: Map<number, string>) => { const u = new Map(m); u.delete(message.id); return u; });
		let success = true;
		const fallback: DeleteMessagesResult = { success: false, deletedIds: [] };

		this.vmailbox.deleteVoicemessages([message.id]).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				success = false;
				console.error(`[VMailbox Widget ${this.widgetId}] deleteMessage failed: HTTP ${err.status} messageId=${message.id}`);
				this.messageErrors.update((m: Map<number, string>) =>
					new Map(m).set(message.id, this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.DELETE_FAILED'))),
				);
				return of(fallback);
			}),
		).subscribe((result: DeleteMessagesResult) => {
			if (!success) return;
			const deleted = new Set<number>(result.deletedIds);
			this.messages.update((msgs: Voicemessage[]) =>
				msgs.filter((m: Voicemessage) => !deleted.has(m.id)),
			);
			if (this.playingMessageId() === message.id) this.stopMessage();
		});
	}

	downloadMessage(message: Voicemessage): void {
		this.messageErrors.update((m: Map<number, string>) => { const u = new Map(m); u.delete(message.id); return u; });
		this.vmailbox.downloadMessage(message.id).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				console.error(`[VMailbox Widget ${this.widgetId}] downloadMessage failed: HTTP ${err.status} messageId=${message.id}`);
				const msg = err.status === 404
					? this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.NO_RECORDING')
					: this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.DOWNLOAD_FAILED'));
				this.messageErrors.update((m) => new Map(m).set(message.id, msg));
				return of(null);
			}),
		).subscribe();
	}

	private loadAllAnnouncementInfo(mailboxNr: string): void {
		this.announcementInfo.set(new Map());
		for (const type of this.announcementTypes) {
			this.vmailbox.getAnnouncementInfo(mailboxNr, type).pipe(
				takeUntilDestroyed(this.destroyRef),
				catchError((err: HttpErrorResponse) => {
					if (!this.isSilent(err)) console.error(`[VMailbox Widget ${this.widgetId}] getAnnouncementInfo failed: HTTP ${err.status} type=${type}`);
					return of(null);
				}),
			).subscribe((info: AnnouncementInfo | null) => {
				if (!info) {
					console.log(`[VMailbox Widget ${this.widgetId}] No announcement info for mailboxNr=${mailboxNr}`);
					return;
				}
				this.announcementInfo.update((m: Map<AnnouncementType, AnnouncementInfo>) =>
					new Map(m).set(type, info),
				);
			});
		}
	}

	playAnnouncement(type: AnnouncementType): void {
		if (!this.widgetId) {
			console.warn('[VMailbox Widget] playAnnouncement — widgetId not set');
			return;
		}

		const nr: string | undefined = this.selectedVoicemail()?.nr;
		if (!nr) {
			console.log(`[VMailbox Widget ${this.widgetId}] Mailbox not selected, cannot play announcement`);
			return;
		}
		this.stopMessage();
		this.stopAnnouncement();

		const existing: string | undefined = this.announcementAudioUrls().get(type);
		if (existing) { this.playingAnnouncementType.set(type); return; }

		this.announcementErrors.update((m: Map<AnnouncementType, string>) => { const u = new Map(m); u.delete(type); return u; });
		this.announcementLoadingType.set(type);
		this.vmailbox.getAnnouncementAudioUrl(this.widgetId, nr, type).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				console.error(`[VMailbox Widget ${this.widgetId}] getAnnouncementAudioUrl failed: HTTP ${err.status} type=${type}`);
				const msg = err.status === 404
					? this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.NO_RECORDING')
					: this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.PLAYBACK_FAILED'));
				this.announcementErrors.update((m: Map<AnnouncementType, string>) => new Map(m).set(type, msg));
				return of(null);
			}),
			finalize(() => this.announcementLoadingType.set(null)),
		).subscribe((url: string | null) => {
			if (!url) {
				console.log(`[VMailbox Widget ${this.widgetId}] Unable to play announcement type=${type}, no audio URL`);
				return;
			}
			this.announcementAudioUrls.update((m: Map<AnnouncementType, string>) =>
				new Map(m).set(type, url),
			);
			this.playingAnnouncementType.set(type);
		});
	}

	stopAnnouncement(): void { this.playingAnnouncementType.set(null); }

	uploadAnnouncement(type: AnnouncementType, event: Event): void {
		const nr: string | undefined = this.selectedVoicemail()?.nr;
		if (!nr) {
			console.log(`[VMailbox Widget ${this.widgetId}] Mailbox not selected, cannot upload announcement`);
			return;
		}
		const input = event.target as HTMLInputElement;
		const file: File | undefined = input.files?.[0];
		if (!file) return;
		this.doUpload(nr, type, file);
		input.value = '';
	}

	deleteAnnouncement(type: AnnouncementType): void {
		if (!this.widgetId) {
			console.warn('[VMailbox Widget] deleteAnnouncement — widgetId not set');
			return;
		}

		const nr: string | undefined = this.selectedVoicemail()?.nr;
		if (!nr) {
			console.log(`[VMailbox Widget ${this.widgetId}] Mailbox not selected, cannot delete announcement`);
			return;
		}
		if (this.playingAnnouncementType() === type) this.stopAnnouncement();
		let success = true;

		this.vmailbox.deleteAnnouncement(this.widgetId, nr, type).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				success = false;
				console.error(`[VMailbox Widget ${this.widgetId}] deleteAnnouncement failed: HTTP ${err.status} type=${type}`);
				this.announcementErrors.update((m: Map<AnnouncementType, string>) =>
					new Map(m).set(type, this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.DELETE_FAILED'))),
				);
				return of(undefined);
			}),
		).subscribe(() => {
			if (!success) return;
			this.announcementInfo.update((m: Map<AnnouncementType, AnnouncementInfo>) =>
				new Map(m).set(type, { announcementId: null, announcementType: type, exists: false }),
			);
			this.announcementAudioUrls.update((m: Map<AnnouncementType, string>) => {
				const updated = new Map(m); updated.delete(type); return updated;
			});
		});
	}

	downloadAnnouncement(type: AnnouncementType): void {
		const nr: string | undefined = this.selectedVoicemail()?.nr;
		if (!nr) {
			console.log(`[VMailbox Widget ${this.widgetId}] Mailbox not selected, cannot download announcement`);
			return;
		}
		this.announcementErrors.update((m: Map<AnnouncementType, string>) => { const u = new Map(m); u.delete(type); return u; });
		this.vmailbox.downloadAnnouncement(nr, type).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				console.error(`[VMailbox Widget ${this.widgetId}] downloadAnnouncement failed: HTTP ${err.status} type=${type}`);
				const msg = err.status === 404
					? this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.NO_RECORDING')
					: this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.DOWNLOAD_FAILED'));
				this.announcementErrors.update((m) => new Map(m).set(type, msg));
				return of(null);
			}),
		).subscribe();
	}

	async startRecording(type: AnnouncementType): Promise<void> {
		const nr: string | undefined = this.selectedVoicemail()?.nr;
		if (!nr) {
			console.log(`[VMailbox Widget ${this.widgetId}] Mailbox not selected, cannot start recording`);
			return;
		}
		if (this.recordingType()) this.stopRecording();
		this.recorderError.set(null);
		this.requestingMic.set(true);

		let stream: MediaStream;
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
		} catch {
			console.error(`[VMailbox Widget ${this.widgetId}] startRecording — mic access denied`);
			this.recorderError.set(this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.MIC_DENIED'));
			this.requestingMic.set(false);
			return;
		}

		this.requestingMic.set(false);
		this.micStream = stream;
		this.recordedChunks = [];
		this.recordingSeconds.set(0);

		const mimeType: string = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
			? 'audio/webm;codecs=opus'
			: 'audio/webm';

		this.mediaRecorder = new MediaRecorder(stream, { mimeType });
		this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
			if (e.data.size > 0) this.recordedChunks.push(e.data);
		};
		this.mediaRecorder.onstop = () => {
			const blob = new Blob(this.recordedChunks, { type: mimeType });
			const file = new File([blob], `recording-${nr}-${type}.webm`, { type: mimeType });
			this.doUpload(nr, type, file);
			this.stopRecorderCleanup();
		};
		this.mediaRecorder.start(100);
		this.recordingType.set(type);
		this.recordingTimer = setInterval(() => {
			this.recordingSeconds.update((s: number) => s + 1);
		}, 1000);
	}

	stopRecording(): void {
		if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
			this.mediaRecorder.stop();
		} else {
			this.stopRecorderCleanup();
		}
	}

	private stopRecorderCleanup(): void {
		if (this.recordingTimer) { clearInterval(this.recordingTimer); this.recordingTimer = null; }
		if (this.micStream) { this.micStream.getTracks().forEach((t: MediaStreamTrack) => t.stop()); this.micStream = null; }
		this.mediaRecorder = null;
		this.recordedChunks = [];
		this.recordingType.set(null);
		this.recordingSeconds.set(0);
	}

	recordingDuration(): string {
		const s: number = this.recordingSeconds();
		return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
	}

	private doUpload(nr: string, type: AnnouncementType, file: File): void {
		if (!this.widgetId) {
			console.warn('[VMailbox Widget] doUpload — widgetId not set');
			return;
		}

		this.uploadingType.set(type);
		this.announcementErrors.update((m: Map<AnnouncementType, string>) => { const u = new Map(m); u.delete(type); return u; });

		this.vmailbox.uploadAnnouncement(this.widgetId, nr, type, file).pipe(
			takeUntilDestroyed(this.destroyRef),
			catchError((err: HttpErrorResponse) => {
				console.error(`[VMailbox Widget ${this.widgetId}] uploadAnnouncement failed: HTTP ${err.status} type=${type}`);
				this.announcementErrors.update((m: Map<AnnouncementType, string>) =>
					new Map(m).set(type, this.toUserMessage(err, this.translate.instant('ASTIMAX.VMAILBOX.WIDGET.ERRORS.UPLOAD_FAILED'))),
				);
				return of(null);
			}),
			finalize(() => this.uploadingType.set(null)),
			switchMap((result: UploadAnnouncementResult | null) => result ? this.vmailbox.getAnnouncementInfo(nr, type) : of(null)),
		).subscribe((info: AnnouncementInfo | null) => {
			if (!info) return;
			this.announcementInfo.update((m: Map<AnnouncementType, AnnouncementInfo>) =>
				new Map(m).set(type, info),
			);
			this.announcementAudioUrls.update((m: Map<AnnouncementType, string>) => {
				const updated = new Map(m); updated.delete(type); return updated;
			});
		});
	}

	formatTimestamp(timestamp: string | undefined): string { return this.vmailbox.formatTimestamp(timestamp); }
	formatDuration(duration: string | undefined): string { return this.vmailbox.formatDuration(duration); }

	announcementLabel(type: AnnouncementType): string {
		const keys: Record<AnnouncementType, string> = {
			temp:    'ASTIMAX.VMAILBOX.WIDGET.ANNOUNCEMENTS.TYPE_TEMP',
			default: 'ASTIMAX.VMAILBOX.WIDGET.ANNOUNCEMENTS.TYPE_DEFAULT',
			busy:    'ASTIMAX.VMAILBOX.WIDGET.ANNOUNCEMENTS.TYPE_BUSY',
			unavail: 'ASTIMAX.VMAILBOX.WIDGET.ANNOUNCEMENTS.TYPE_UNAVAIL',
		};
		return this.translate.instant(keys[type]);
	}

	callBack(message: Voicemessage): void {
		if (!this.widgetId) {
			console.warn('[VMailbox Widget] callBack — widgetId not set');
			return;
		}

		const { number } = this.vmailbox.parseCallerId(message.callerid);
		if (!number) {
			console.warn(`[VMailbox Widget ${this.widgetId}] callBack — no phone number in callerid: ${message.callerid}`);
			return;
		}

		console.log(`[VMailbox Widget ${this.widgetId}] callBack → number=${number}`);
		this.vmailbox.initiateCall(this.widgetId, number);
	}

	private autoMarkRead(message: Voicemessage): void {
		if (message.isnew) this.markAsRead(message);
	}

	private buildPlaceholderMailbox(nr: string): AsteriskVoicemail {
		return {
			id: 0, nr, name: null, language: null,
			email: null, pageremail: null, attach: null, delete: null,
			operator: null, envelope: null, sayduration: null, saycid: null,
		};
	}
}
